import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getSuperadminTenants, impersonateTenant, endImpersonation } from '../services/api';

interface Tenant {
  id: string;
  name: string;
  subdomain: string;
  status: string;
  plan: string;
  agent_count: number;
}

const TenantPicker: React.FC = () => {
  const { user } = useAuth();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [currentTenantName, setCurrentTenantName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const isSuperadmin = user?.roles?.includes('superadmin');

  useEffect(() => {
    if (!isSuperadmin) return;

    const stored = localStorage.getItem('anchor_impersonating');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setIsImpersonating(true);
        setCurrentTenantName(parsed.tenantName);
      } catch { /* ignore */ }
    }

    getSuperadminTenants()
      .then((data) => setTenants(data))
      .catch((err) => console.error('Failed to load tenants:', err));
  }, [isSuperadmin]);

  if (!isSuperadmin) return null;

  const handleSelect = async (tenant: Tenant) => {
    setLoading(true);
    try {
      const result = await impersonateTenant(tenant.id, `Dashboard impersonation: ${tenant.name}`);
      localStorage.setItem('anchor_impersonating', JSON.stringify({
        tenantId: tenant.id,
        tenantName: tenant.name,
        token: result.token,
      }));
      setIsImpersonating(true);
      setCurrentTenantName(tenant.name);
      setIsOpen(false);
      window.location.reload();
    } catch (err) {
      console.error('Impersonation failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEndImpersonation = async () => {
    setLoading(true);
    try {
      await endImpersonation();
      localStorage.removeItem('anchor_impersonating');
      setIsImpersonating(false);
      setCurrentTenantName(null);
      window.location.reload();
    } catch (err) {
      console.error('Failed to end impersonation:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      {isImpersonating && (
        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-2 px-3 py-1.5 bg-amber-500/20 border border-amber-500/40 rounded-lg">
            <div className="h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
            <span className="text-xs text-amber-300 font-medium">
              Viewing: {currentTenantName}
            </span>
          </div>
          <button
            onClick={handleEndImpersonation}
            disabled={loading}
            className="px-3 py-1.5 text-xs font-medium text-white bg-anchor-blue/20 border border-anchor-blue/40 rounded-lg hover:bg-anchor-blue/30 transition-colors disabled:opacity-50"
          >
            Back to original
          </button>
        </div>
      )}

      {!isImpersonating && (
        <div>
          <button
            onClick={() => setIsOpen(!isOpen)}
            disabled={loading}
            className="px-3 py-1.5 text-xs font-medium text-anchor-muted bg-white/[0.04] border border-white/[0.08] rounded-lg hover:bg-white/[0.08] transition-colors disabled:opacity-50"
          >
            Switch Tenant
          </button>

          {isOpen && (
            <div className="absolute right-0 top-full mt-2 w-72 bg-anchor-card border border-white/[0.08] rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto">
              {tenants.map((tenant) => (
                <button
                  key={tenant.id}
                  onClick={() => handleSelect(tenant)}
                  className="w-full px-4 py-3 text-left hover:bg-white/[0.04] transition-colors border-b border-white/[0.04] last:border-b-0"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-white">{tenant.name}</div>
                      <div className="text-xs text-anchor-muted">{tenant.subdomain} &middot; {tenant.plan}</div>
                    </div>
                    <div className="text-xs text-anchor-muted">
                      {tenant.agent_count} agent{tenant.agent_count !== 1 ? 's' : ''}
                    </div>
                  </div>
                </button>
              ))}
              {tenants.length === 0 && (
                <div className="px-4 py-3 text-sm text-anchor-muted">No tenants found</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TenantPicker;
